/**
 * The DSAR slice of the wire — the employee's own copy of their own data (Tier-3
 * U8, `openapi.yaml`'s `DSAR` tag).
 *
 * Hand-written to mirror the contract, exactly as `geo.ts` and `hr.ts` describe for
 * their slices: transcribe what the screen reads, not the whole document. A field
 * the contract marks nullable is `T | null` here; `exactOptionalPropertyTypes` is
 * on, so an absent value and an explicit `null` stay different facts.
 *
 * ── There is no `employee_id` anywhere in this file, and that is the shape ──
 *
 * `DsarRequestCreateRequest` has one member. Not "we do not send one" — there is no
 * argument position on any function here that could name a person, so a client bug
 * cannot ask for somebody else's data. The same discipline `geo.ts` keeps for
 * `GeoCheckinRequest` and `hr.ts` for a leave request.
 *
 * ── Nothing here keeps a download URL ───────────────────────────────────────
 *
 * `requestDsarDownload` hands the URL to `navigate` and returns facts, never the
 * link — the technique `web/src/reports/download.ts` established and the reason it
 * gives applies with more force here: the file is one person's whole history, the
 * link is a bearer credential the object store redeems without re-checking
 * anything, and a `DownloadState` with no `url` member makes a stale link, a "copy
 * link" button and a `mailto:` carrying one **unrenderable** rather than merely
 * discouraged.
 */

import type { ApiClient } from './client';
import { WorkforceApiError } from './errors';

/** `DsarRequestRecord.kind`. Only `export` is implemented in this build. */
export type DsarKind = 'export' | 'erasure';

/** `DsarRequestRecord.status` — the same four words `wp_reports` uses. */
export type DsarStatus = 'pending' | 'running' | 'done' | 'failed';

/** `DsarRequestRecord` of `openapi.yaml`. No `file_key`: the object key is not published. */
export interface DsarRequestRecord {
  readonly id: string;
  readonly kind: DsarKind;
  readonly status: DsarStatus;
  readonly requested_at: string;
  readonly completed_at: string | null;
  /** When the FILE stops being served. Not the link's expiry. */
  readonly expires_at: string | null;
  readonly download_available: boolean;
  readonly counts: Readonly<Record<string, number>>;
  readonly failure_code: string | null;
}

/**
 * `DsarPolicy` — the figures the server enforces, sent so the screen never invents
 * one. Three of the four are lifetimes and they are three different numbers; the
 * screen is required by `web/shapes/my-data.md` §4 not to collapse them.
 */
export interface DsarPolicy {
  readonly daily_request_cap: number;
  readonly completion_target_hours: number;
  readonly file_available_days: number;
  readonly download_url_ttl_seconds: number;
}

export interface DsarRequestListResponse {
  readonly data: readonly DsarRequestRecord[];
  readonly policy: DsarPolicy;
}

export interface DsarRequestResponse {
  readonly data: DsarRequestRecord;
}

/** The `mode=json` body of the download. Handled and dropped; never stored. */
export interface DsarDownloadGrant {
  readonly download_url: string;
  readonly expires_at: string;
  readonly filename: string;
}

export const DSAR_MY_REQUESTS_PATH = '/api/v1/dsar/my-requests';

/**
 * `mode=json`, always.
 *
 * `api/client.ts` sets `redirect: 'error'`, and a 302 to the object store's host
 * would drop the session cookie on the way. The JSON body hands back the same
 * credential and says so out loud — `openapi.yaml` publishes both modes for exactly
 * this client.
 */
export function dsarDownloadPath(requestId: string): string {
  return `${DSAR_MY_REQUESTS_PATH}/${encodeURIComponent(requestId)}/download?mode=json`;
}

/** Which statuses are still moving — the screen polls while one of these is on it. */
export function isInFlight(request: DsarRequestRecord): boolean {
  return request.status === 'pending' || request.status === 'running';
}

/** How the page navigates to a signed URL. Injected so a test drives it without jsdom. */
export type Navigate = (url: string) => void;

/**
 * What a screen may remember about a download that has already happened.
 *
 * Deliberately not a `DsarDownloadGrant`: the grant is spent and dropped.
 */
export type DsarDownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly requestId: string }
  | {
      readonly status: 'granted';
      readonly requestId: string;
      readonly filename: string;
      /** When the credential just spent stops being accepted. A fact, not a link. */
      readonly expiresAt: string;
    }
  | {
      readonly status: 'refused';
      readonly requestId: string;
      readonly error: WorkforceApiError;
    };

export interface DsarDownloadOutcome {
  readonly state: DsarDownloadState;
  /** True when the row's state was provably stale and the screen should re-read it. */
  readonly staleStatus: boolean;
}

/**
 * Ask for a signed URL, spend it, and keep only the facts.
 *
 * `409 WORKFORCE_REPORT_NOT_READY` and `410 WORKFORCE_REPORT_EXPIRED` come back as
 * `staleStatus: true`: both mean the state this control was derived from is out of
 * date, and the honest response is to render the server's own answer and re-read the
 * list rather than guess which state the row moved to.
 *
 * A cross-company id — or a colleague's — answers `404`, exactly as a non-existent
 * one does, and this function does not tell them apart because there is nothing to
 * tell apart: the server spends a `404` to hide the distinction, and a client that
 * rendered two sentences would leak it back.
 */
export async function requestDsarDownload(
  client: ApiClient,
  requestId: string,
  navigate: Navigate,
): Promise<DsarDownloadOutcome> {
  try {
    // The `{ data: … }` envelope every route on this build sends, named rather than
    // assumed: a client typed as the bare grant would read `download_url` off the
    // envelope, get `undefined`, and navigate to the string "undefined".
    const envelope = await client.request<{ data: DsarDownloadGrant }>(dsarDownloadPath(requestId));
    const grant = envelope.data;
    navigate(grant.download_url);
    return {
      state: {
        status: 'granted',
        requestId,
        filename: grant.filename,
        expiresAt: grant.expires_at,
      },
      staleStatus: false,
    };
  } catch (cause) {
    const error =
      cause instanceof WorkforceApiError
        ? cause
        : new WorkforceApiError({
            code: 'CLIENT_TRANSPORT_FAILED',
            message: cause instanceof Error ? cause.message : 'download failed',
            status: 0,
          });
    return {
      state: { status: 'refused', requestId, error },
      staleStatus:
        error.code === 'WORKFORCE_REPORT_NOT_READY' || error.code === 'WORKFORCE_REPORT_EXPIRED',
    };
  }
}

/* --------------------------------------------------- the admin queue (U10) */

export const DSAR_REQUESTS_PATH = '/api/v1/dsar/requests';

/**
 * `DsarQueueRow` of `openapi.yaml` — a `DsarRequestRecord` plus the four fields only
 * the HR queue publishes.
 *
 * `result` is `unknown`-shaped on purpose. It is the erasure's own record — counts
 * per class, every legally-retained record NAMED with its ground, the audit tables
 * kept as evidence, and the subject's notice in two languages — and the contract
 * declares it a free-form object. Typing it here would be a second, weaker copy of a
 * shape the server owns, and the first thing to drift when a class is added.
 */
export interface DsarQueueRow extends DsarRequestRecord {
  readonly employee_id: string;
  readonly result: Readonly<Record<string, unknown>>;
  readonly legal_hold_count: number;
  readonly erasure_progress: readonly string[];
}

export interface DsarQueueListResponse {
  readonly data: readonly DsarQueueRow[];
  readonly policy: DsarPolicy;
}

export interface DsarQueueRowResponse {
  readonly data: DsarQueueRow;
}

/** `GET /api/v1/dsar/requests` — every request of the caller's company. */
export async function fetchDsarQueue(client: ApiClient): Promise<DsarQueueListResponse> {
  return client.request<DsarQueueListResponse>(DSAR_REQUESTS_PATH);
}

/**
 * `POST /api/v1/dsar/requests/{id}/execute` — run the erasure and wait for it.
 *
 * There is no `employee_id` argument, and there is no version of this function that
 * takes one: the subject is read from the request row on the server. The one
 * argument is the id of a row an admin picked off a list they were authorised to
 * read, which is as close to "you cannot point this at the wrong person" as a client
 * can get.
 *
 * It resolves when the deletion has finished, not when it has been accepted. The
 * screen must therefore keep the button disabled for the whole call and must not
 * offer a retry until it has an answer — a second execution of a half-finished
 * erasure is safe (the progress marker makes it resume), but a person clicking twice
 * because nothing moved is a person who does not know what happened.
 */
export async function executeDsarErasure(
  client: ApiClient,
  requestId: string,
): Promise<DsarQueueRow> {
  const envelope = await client.request<DsarQueueRowResponse>(
    `${DSAR_REQUESTS_PATH}/${encodeURIComponent(requestId)}/execute`,
    { method: 'POST' },
  );
  return envelope.data;
}

/** `POST /api/v1/dsar/my-requests` with `kind: 'erasure'` — the subject's own ask. */
export async function createDsarRequest(
  client: ApiClient,
  kind: DsarKind,
): Promise<DsarRequestRecord> {
  const envelope = await client.request<DsarRequestResponse>(DSAR_MY_REQUESTS_PATH, {
    method: 'POST',
    body: { kind },
  });
  return envelope.data;
}
